import { FC, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Button, Typography, Divider, useTheme } from '@mui/material';
import { UploadedFileItem } from '@ldt/features/orders/components/uploaded_file_item';
import { LdtUploadedFileFieldsFragment, UploadedFileCategory } from 'src/types';

interface FileUploadProps {
  label: string;
  category: UploadedFileCategory;
  required?: boolean;
  helperText?: string;
  validationText?: string;
  onFileSelected?: (file: File, category: UploadedFileCategory) => void;
  onFileRemoved?: (fileId: string) => void;
  accept?: string;
  allowMultiple?: boolean;
  showDivider?: boolean;
  files?: LdtUploadedFileFieldsFragment[];
  isFileDeleting?: (fileId: string) => boolean;
}

export const FileUpload: FC<FileUploadProps> = ({
  label,
  category,
  required = false,
  helperText,
  validationText,
  onFileSelected,
  onFileRemoved,
  accept = '.pdf,.png,.jpg,.jpeg',
  allowMultiple = true,
  showDivider = false,
  files = [],
  isFileDeleting,
}) => {
  const theme = useTheme();
  // Convert file extensions to MIME types for react-dropzone
  const extensionToMimeType: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
  };

  const acceptedFileTypes = accept
    .split(',')
    .map((ext) => ext.trim())
    .reduce((acc, ext) => {
      const mimeType = extensionToMimeType[ext] || ext;
      return { ...acc, [mimeType]: [ext] };
    }, {});

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const newFiles = allowMultiple
        ? acceptedFiles
        : acceptedFiles.slice(0, 1);

      // If allowMultiple is false and there's an existing file, remove it first
      if (!allowMultiple && files.length >= 1 && onFileRemoved) {
        const existingFile = files[0];
        if (existingFile?.id) {
          // Remove the existing file (will cancel if uploading, or delete if completed)
          onFileRemoved(existingFile.id);
        }
      }

      // Now handle the new file(s)
      for (const file of newFiles) {
        // Notify parent component with the File object
        // Parent will convert it to UploadedFile
        if (onFileSelected) {
          onFileSelected(file, category);
        }
      }
    },
    [allowMultiple, onFileSelected, onFileRemoved, category, files],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: allowMultiple ? undefined : 1,
    noClick: false,
    noDrag: true, // Disable drag and drop, only allow click
    disabled: false, // Always enabled - replacement handled in onDrop
  });

  return (
    <Box
      data-testid={`file-upload-${category.toLowerCase()}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: theme.typography.fontWeightMedium,
            }}
          >
            {label}
            {required && <Typography component='span'>*</Typography>}
          </Typography>
          <Box
            {...getRootProps()}
            sx={{
              display: 'inline-block',
              alignSelf: 'flex-start',
              cursor: 'pointer',
            }}
          >
            <input {...getInputProps()} />
            <Button
              variant='contained'
              size='small'
              data-testid={`file-upload-${category.toLowerCase()}-button`}
            >
              UPLOAD
            </Button>
          </Box>
        </Box>

        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
            fontSize: theme.typography.caption.fontSize,
            display: 'block',
            minHeight: '0.75rem',
            lineHeight: '0.75rem',
            m: 0,
            p: 0,
          }}
        >
          {helperText || '\u00A0'}
        </Typography>

        {files.length > 0 && (
          <Box
            data-testid={`file-upload-${category.toLowerCase()}-files`}
            sx={{
              mt: 1,
              minWidth: '120px',
              maxHeight: '120px',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {files.map((uploadedFile, index) => (
              <UploadedFileItem
                key={uploadedFile.id || index}
                file={uploadedFile}
                onRemove={
                  onFileRemoved
                    ? () => onFileRemoved(uploadedFile.id)
                    : undefined
                }
                index={index}
                isRemoving={isFileDeleting?.(uploadedFile.id)}
              />
            ))}
          </Box>
        )}

        <Typography
          variant='caption'
          color='error'
          sx={{
            fontSize: theme.typography.caption.fontSize,
            display: 'block',
            minHeight: '0.75rem',
            lineHeight: '0.75rem',
            m: 0,
            p: 0,
            mt: 0.5,
          }}
        >
          {validationText || '\u00A0'}
        </Typography>
      </Box>
      {showDivider ? (
        <Divider
          className='file-upload-divider'
          orientation='vertical'
          sx={{ height: '26px', display: { xs: 'none', sm: 'block' } }}
        />
      ) : (
        <Box
          sx={{
            width: '1px',
            height: '26px',
            display: { xs: 'none', sm: 'block' },
            visibility: 'hidden',
          }}
        />
      )}
    </Box>
  );
};
